/**
 * AdminPaxCommSituations — CRUD page for Passenger Communication Situations
 * Route: /admin/pax-comm-situations
 * Roles: admin, superadmin
 */
import { useState } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, ConfirmDialog, LoadingState } from '../components/admin';
import DataTable from '../components/admin/DataTable';
import BilingualTextField from '../components/admin/BilingualTextField';
import DynamicListInput from '../components/admin/DynamicListInput';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  usePaxCommSituations,
  useCreatePaxCommSituation,
  useUpdatePaxCommSituation,
  useDeletePaxCommSituation,
} from '../api/admin/cabinModules';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Plus, MessageSquare, Edit, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';

const CATEGORIES = [
  { value: 'vip', labelTr: 'VIP', labelEn: 'VIP' },
  { value: 'difficult', labelTr: 'Zor Yolcu', labelEn: 'Difficult Passenger' },
  { value: 'family', labelTr: 'Aile', labelEn: 'Family' },
  { value: 'disability', labelTr: 'Engelli Yolcu', labelEn: 'Disability' },
  { value: 'language', labelTr: 'Dil Engeli', labelEn: 'Language Barrier' },
  { value: 'complaint', labelTr: 'Şikayet', labelEn: 'Complaint' },
];

const TONES = [
  { value: 'friendly', labelTr: 'Samimi', labelEn: 'Friendly' },
  { value: 'professional', labelTr: 'Profesyonel', labelEn: 'Professional' },
  { value: 'empathetic', labelTr: 'Empatik', labelEn: 'Empathetic' },
  { value: 'firm', labelTr: 'Kararlı', labelEn: 'Firm' },
];

const EMPTY_RESPONSE = () => ({
  id: '',
  tone: 'professional',
  response_tr: '',
  response_en: '',
});

const EMPTY_FORM = {
  id: '',
  category: '',
  title_tr: '',
  title_en: '',
  context_tr: '',
  context_en: '',
  responses: [EMPTY_RESPONSE()],
  tips_tr: '',
  tips_en: '',
  is_active: true,
};

export default function AdminPaxCommSituations() {
  const { lang, t } = useLanguage();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ category: '', is_active: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const activeFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '')
  );

  const { data, isLoading } = usePaxCommSituations(page, activeFilters);
  const createMutation = useCreatePaxCommSituation();
  const updateMutation = useUpdatePaxCommSituation();
  const deleteMutation = useDeletePaxCommSituation();

  const items = data?.items || data?.situations || [];
  const total = data?.total || 0;
  const totalPages = data?.pages || Math.ceil(total / 20) || 1;

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id || row._id);
    setForm({
      id: row.id || row._id || '',
      category: row.category || '',
      title_tr: row.title_tr || '',
      title_en: row.title_en || '',
      context_tr: row.context_tr || '',
      context_en: row.context_en || '',
      responses: row.responses?.length ? row.responses : [EMPTY_RESPONSE()],
      tips_tr: row.tips_tr || '',
      tips_en: row.tips_en || '',
      is_active: row.is_active !== false,
    });
    setModalOpen(true);
  };

  const fieldChange = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async () => {
    if (!form.title_tr || !form.title_en) {
      toast.error(t('admin.paxComm.validationTitleRequired'));
      return;
    }
    if (!form.category) {
      toast.error(t('admin.paxComm.validationCategoryRequired'));
      return;
    }
    if (form.responses.length < 1) {
      toast.error(t('admin.paxComm.validationResponseRequired'));
      return;
    }
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: form });
        toast.success(t('admin.paxComm.updateSuccess'));
      } else {
        await createMutation.mutateAsync(form);
        toast.success(t('admin.paxComm.createSuccess'));
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(deleteTarget.id || deleteTarget._id);
      toast.success(t('admin.paxComm.deleteSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  const columns = [
    {
      key: 'title_tr',
      label: t('admin.paxComm.colTitle'),
      sortable: true,
      render: (_, row) => (
        <span className="font-medium text-gray-800 block max-w-[220px] truncate">{row.title_tr}</span>
      ),
    },
    {
      key: 'category',
      label: t('admin.paxComm.colCategory'),
      render: (_, row) => {
        const cat = CATEGORIES.find((c) => c.value === row.category);
        return <Badge variant="secondary" className="text-xs">{(lang === 'tr' ? cat?.labelTr : cat?.labelEn) || row.category}</Badge>;
      },
    },
    {
      key: 'response_count',
      label: t('admin.paxComm.colResponses'),
      render: (_, row) => <Badge variant="outline">{row.responses?.length ?? row.response_count ?? 0}</Badge>,
    },
    {
      key: 'is_active',
      label: t('admin.paxComm.colActive'),
      render: (_, row) => (
        <Badge className={row.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
          {row.is_active ? (lang === 'tr' ? 'Aktif' : 'Active') : (lang === 'tr' ? 'Pasif' : 'Inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-7 px-2">
            <Edit size={14} className="mr-1" />
            {t('admin.btn_edit')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} className="h-7 px-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50">
            <Trash2 size={14} className="mr-1" />
            {t('admin.btn_delete')}
          </Button>
        </div>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('admin.nav.cabinAcademy'), href: '/admin/cabin-academy' },
          { label: t('admin.nav.paxComm') },
        ]}
        title={t('admin.paxComm.title')}
        subtitle={t('admin.paxComm.subtitle')}
        icon={MessageSquare}
        actions={
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button onClick={openCreate} className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white">
              <Plus size={16} />
              {t('admin.paxComm.createBtn')}
            </Button>
          </motion.div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-44 h-9 text-sm">
            <SelectValue placeholder={t('admin.paxComm.filterCategory')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{lang === 'tr' ? c.labelTr : c.labelEn}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.is_active} onValueChange={(v) => setFilters((f) => ({ ...f, is_active: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-32 h-9 text-sm">
            <SelectValue placeholder={t('admin.paxComm.filterStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
            <SelectItem value="true">{lang === 'tr' ? 'Aktif' : 'Active'}</SelectItem>
            <SelectItem value="false">{lang === 'tr' ? 'Pasif' : 'Inactive'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <LoadingState /> : (
        <DataTable
          columns={columns}
          data={items}
          rowKey={(r) => r.id || r._id}
          emptyTitle={t('admin.paxComm.emptyTitle')}
          emptyDescription={t('admin.paxComm.emptyDesc')}
        />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('admin.prev_page')}</Button>
          <span className="text-sm text-gray-600">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t('admin.next_page')}</Button>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? t('admin.paxComm.editTitle') : t('admin.paxComm.createTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* ID + Category */}
            <div className="grid grid-cols-2 gap-3">
              {!editingId && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">ID (slug) <span className="text-rose-500">*</span></Label>
                  <Input value={form.id} onChange={(e) => fieldChange('id', e.target.value)} placeholder="passenger_complaint_01" className="text-sm font-mono" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">
                  {t('admin.paxComm.fieldCategory')} <span className="text-rose-500">*</span>
                </Label>
                <Select value={form.category} onValueChange={(v) => fieldChange('category', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder={t('admin.select_placeholder')} /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{lang === 'tr' ? c.labelTr : c.labelEn}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title */}
            <BilingualTextField
              labelTr={t('admin.paxComm.fieldTitleTr')}
              labelEn={t('admin.paxComm.fieldTitleEn')}
              nameTr="title_tr"
              nameEn="title_en"
              valueTr={form.title_tr}
              valueEn={form.title_en}
              onChange={fieldChange}
              required
            />

            {/* Context */}
            <BilingualTextField
              labelTr={t('admin.paxComm.fieldContextTr')}
              labelEn={t('admin.paxComm.fieldContextEn')}
              nameTr="context_tr"
              nameEn="context_en"
              valueTr={form.context_tr}
              valueEn={form.context_en}
              onChange={fieldChange}
              multiline
              rows={3}
            />

            {/* Responses */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-gray-600">{t('admin.paxComm.fieldResponses')}</Label>
              <DynamicListInput
                value={form.responses}
                onChange={(v) => fieldChange('responses', v)}
                emptyItem={EMPTY_RESPONSE}
                addLabel={lang === 'tr' ? 'Yanıt Ekle' : 'Add Response'}
                min={1}
                max={10}
                renderItem={(item, idx, onItemChange) => (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Ton' : 'Tone'}</Label>
                      <Select value={item.tone} onValueChange={(v) => onItemChange({ ...item, tone: v })}>
                        <SelectTrigger className="h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TONES.map((tn) => (
                            <SelectItem key={tn.value} value={tn.value}>{lang === 'tr' ? tn.labelTr : tn.labelEn}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Textarea
                        value={item.response_tr}
                        onChange={(e) => onItemChange({ ...item, response_tr: e.target.value })}
                        placeholder="Türkçe yanıt..."
                        rows={3}
                        className="text-sm"
                      />
                      <Textarea
                        value={item.response_en}
                        onChange={(e) => onItemChange({ ...item, response_en: e.target.value })}
                        placeholder="English response..."
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Tips (single text TR + EN) */}
            <BilingualTextField
              labelTr={t('admin.paxComm.fieldTips')}
              labelEn={t('admin.paxComm.fieldTips')}
              nameTr="tips_tr"
              nameEn="tips_en"
              valueTr={form.tips_tr}
              valueEn={form.tips_en}
              onChange={fieldChange}
              multiline
              rows={3}
            />

            {/* Active */}
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => fieldChange('is_active', v)} id="pcs-is-active" />
              <Label htmlFor="pcs-is-active" className="text-sm font-medium cursor-pointer">{t('admin.paxComm.fieldActive')}</Label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>{t('admin.btn_cancel')}</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-cyan-500 hover:bg-cyan-600 text-white">
              {isSaving ? t('admin.btn_loading') : t('admin.btn_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('admin.paxComm.deleteTitle')}
        description={t('admin.paxComm.deleteDesc')}
        onConfirm={handleDeleteConfirm}
        loading={deleteMutation.isPending}
      />
    </AdminLayout>
  );
}
